import { Skeleton, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useUserState } from '~/atoms/UserState'
import { useGetCallsReport } from '~/queries/reportingQueries'
import { DateRange } from '~/types/Shared'

import { CallsReportRequest } from '@mangovoice/types'

import Heatmap, {
  allDayTimeLabels,
  dayLabels,
  daytimeTimeLabels,
  HeatmapData,
} from '../Shared/Heatmap'
import { toLocalDateTimeString } from '../Shared/LocalTimeHelper'

interface AnsweredCallHeatmapProps {
  dateTimeRange: DateRange
  timeRangeMode: 'all-day' | 'daytime-hours'
  baseTime?: Date
}

interface AnsweredCallData {
  dayOfWeek: number
  hour: number
  answeredCalls: number
  totalCalls: number
}

export default function AnsweredCallHeatmap({
  dateTimeRange,
  timeRangeMode,
  baseTime,
}: AnsweredCallHeatmapProps) {
  // Compute baseTime only once per render if not provided.
  const now = baseTime ?? useMemo(() => new Date(), [dateTimeRange])
  const beforeDate = dateTimeRange?.before ?? now
  const afterDate = dateTimeRange?.after ?? now

  const adjustedBefore = new Date(
    beforeDate.getFullYear(),
    beforeDate.getMonth(),
    beforeDate.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
  )
  const adjustedAfter = new Date(
    afterDate.getFullYear(),
    afterDate.getMonth(),
    afterDate.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
  )
  const startDateStr = toLocalDateTimeString(adjustedAfter)
  const endDateStr = toLocalDateTimeString(adjustedBefore)
  const { pbxId } = useUserState()
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const callsReportRequest: CallsReportRequest = {
    filters: {
      startDate: startDateStr,
      endDate: endDateStr,
      pbxIds: [pbxId ?? 0],
      direction: 'inbound',
      timezone: timeZone,
    },
    metrics: [{ field: 'calls', type: 'count' }],
    groupBy: ['hour'],
    splitBy: ['answered'],
  }

  const {
    data: callsReport,
    isLoading,
    error,
  } = useGetCallsReport({
    reportRequest: callsReportRequest,
  })

  const timeLabels =
    timeRangeMode === 'all-day' ? allDayTimeLabels : daytimeTimeLabels

  const startHour = parseLabelToHour(timeLabels[0])
  const endHour = parseLabelToHour(timeLabels[timeLabels.length - 1])

  const heatmapData: HeatmapData<AnsweredCallData>[] = useMemo(() => {
    if (!callsReport?.data) return []

    const weekdayAnsweredCalls = Array.from({ length: 7 }, () =>
      Array.from({ length: timeLabels.length }, () => 0),
    )
    const weekdayTotalCalls = Array.from({ length: 7 }, () =>
      Array.from({ length: timeLabels.length }, () => 0),
    )

    callsReport.data.forEach((entry) => {
      const dateObj = new Date(entry.date)
      const hour = dateObj.getHours()
      const dayOfWeek = dateObj.getDay()

      if (hour >= startHour && hour <= endHour) {
        const colIndex = hour - startHour
        const answeredCount = (entry['calls/true'] as number) ?? 0
        const missedCount = (entry['calls/false'] as number) ?? 0
        const total = answeredCount + missedCount

        weekdayAnsweredCalls[dayOfWeek][colIndex] += answeredCount
        weekdayTotalCalls[dayOfWeek][colIndex] += total
      }
    })

    const flattenedData: HeatmapData<AnsweredCallData>[] = []
    for (let day = 0; day < 7; day++) {
      for (let col = 0; col < timeLabels.length; col++) {
        const answeredC = weekdayAnsweredCalls[day][col]
        const totalC = weekdayTotalCalls[day][col]

        flattenedData.push({
          value: answeredC,
          data: {
            dayOfWeek: day,
            hour: col,
            answeredCalls: answeredC,
            totalCalls: totalC,
          },
        })
      }
    }

    return flattenedData
  }, [callsReport?.data, timeLabels, startHour, endHour])

  function formatValue(
    dayLabel: string,
    timeLabel: string,
    _value: number,
    rowData: AnsweredCallData,
  ) {
    return `Answered: ${rowData.answeredCalls} / ${rowData.totalCalls} calls\n(${dayLabel}, ${timeLabel})`
  }

  if (isLoading) return <Skeleton height="400px" />
  if (error) return <Text>Error loading answered calls</Text>
  if (!callsReport?.data?.length) return <Text>No answered call data</Text>

  return (
    <Heatmap
      data={heatmapData}
      yAxisLabels={dayLabels}
      xAxisLabels={timeLabels}
      title="Answered Calls"
      colorScheme="green.600"
      formatValue={formatValue}
    />
  )
}

function parseLabelToHour(label: string): number {
  const [hourStr, ampm] = label.split(' ')
  let hourNum = parseInt(hourStr, 10)
  if (ampm?.toLowerCase() === 'pm' && hourNum !== 12) hourNum += 12
  else if (ampm?.toLowerCase() === 'am' && hourNum === 12) hourNum = 0
  return hourNum
}
